import React, { useEffect, useState } from "react";
import { http, formatError, pruneEmpty } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, ClipboardList, Send, Eye } from "lucide-react";
import { toast } from "sonner";

export default function OwnerProposals() {
  const [proposals, setProposals] = useState([]);
  const [viewing, setViewing] = useState(null);

  async function load() {
    const { data } = await http.get("/ops/proposals");
    setProposals(data);
  }
  useEffect(() => { load(); }, []);

  async function markStatus(id, status) {
    try { await http.patch(`/ops/proposals/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }

  const totalPipeline = proposals.filter((p) => ["sent", "viewed"].includes(p.status)).reduce((s, p) => s + (p.total_cents || 0), 0);
  const acceptedRate = proposals.length ? Math.round(proposals.filter((p) => p.status === "accepted").length / proposals.length * 100) : 0;

  return (
    <div className="space-y-6" data-testid="owner-proposals">
      <div className="flex justify-between items-center">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Proposals</div>
          <h1 className="font-display text-3xl font-bold text-primary">Proposal Generation</h1>
        </div>
        <NewProposalDialog onCreated={load} />
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Stat label="Total proposals" value={proposals.length} />
        <Stat label="In-flight value" value={`$${(totalPipeline / 100).toLocaleString()}`} accent="secondary" />
        <Stat label="Acceptance rate" value={`${acceptedRate}%`} />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {proposals.length === 0 && <Card className="border-dashed md:col-span-2"><CardContent className="p-10 text-center text-muted-foreground">No proposals yet.</CardContent></Card>}
        {proposals.map((p) => (
          <Card key={p.id} className="border-border/60"><CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-display font-semibold text-primary text-base">{p.title}</div>
                <div className="text-xs text-muted-foreground mt-1">{p.intro?.slice(0, 100)}{p.intro?.length > 100 ? "…" : ""}</div>
              </div>
              <Badge className={
                p.status === "accepted" ? "bg-secondary/10 text-secondary border border-secondary/20" :
                p.status === "declined" ? "bg-red-50 text-red-700 border border-red-200" :
                p.status === "sent" || p.status === "viewed" ? "bg-amber-50 text-amber-700 border border-amber-200" :
                "bg-muted text-muted-foreground border border-border"
              }>{p.status}</Badge>
            </div>
            <div className="mt-3 flex items-baseline justify-between">
              <span className="text-xs text-muted-foreground">{p.line_items?.length || 0} line item(s)</span>
              <span className="font-display text-2xl font-bold text-primary">${(p.total_cents / 100).toLocaleString()}</span>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setViewing(p)} data-testid={`proposal-view-${p.id}`}><Eye size={12} className="mr-1" /> View</Button>
              {p.status === "draft" && <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={() => markStatus(p.id, "sent")}><Send size={12} className="mr-1" /> Send</Button>}
              {(p.status === "sent" || p.status === "viewed") && (
                <>
                  <Button size="sm" className="bg-secondary hover:bg-secondary/90" onClick={() => markStatus(p.id, "accepted")}>Accepted</Button>
                  <Button size="sm" variant="ghost" onClick={() => markStatus(p.id, "declined")}>Declined</Button>
                </>
              )}
            </div>
          </CardContent></Card>
        ))}
      </div>

      <ProposalViewer proposal={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}

function Stat({ label, value, accent }) {
  const cls = accent === "secondary" ? "text-secondary" : "text-primary";
  return <Card className="border-border/60"><CardContent className="p-5">
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`font-display text-3xl font-bold mt-1 ${cls}`}>{value}</div>
  </CardContent></Card>;
}

function ProposalViewer({ proposal, onClose }) {
  if (!proposal) return null;
  return (
    <Dialog open={!!proposal} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{proposal.title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {proposal.intro && <p className="text-sm text-muted-foreground italic">{proposal.intro}</p>}
          <table className="w-full text-sm border rounded overflow-hidden">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="text-left p-2">Description</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Unit</th><th className="text-right p-2">Total</th></tr>
            </thead>
            <tbody>
              {(proposal.line_items || []).map((li, i) => (
                <tr key={i} className="border-t">
                  <td className="p-2">{li.description}</td>
                  <td className="p-2 text-right">{li.quantity}</td>
                  <td className="p-2 text-right">${(li.unit_price_cents / 100).toFixed(2)}</td>
                  <td className="p-2 text-right font-semibold">${(li.unit_price_cents * li.quantity / 100).toLocaleString()}</td>
                </tr>
              ))}
              <tr className="border-t bg-muted/30">
                <td colSpan={3} className="p-2 text-right font-display font-semibold">Total</td>
                <td className="p-2 text-right font-display font-bold text-primary text-lg">${(proposal.total_cents / 100).toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
          {proposal.valid_until && <div className="text-xs text-muted-foreground">Valid until {proposal.valid_until}</div>}
          {proposal.notes && <div className="text-xs text-muted-foreground border-t pt-2">{proposal.notes}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewProposalDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", intro: "", valid_until: "", notes: "" });
  const [lines, setLines] = useState([{ description: "", quantity: 1, unit_price_cents: 0 }]);
  const total = lines.reduce((s, l) => s + Number(l.quantity) * Number(l.unit_price_cents), 0);

  function updateLine(i, k, v) {
    setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: k === "description" ? v : Number(v) } : l));
  }

  async function submit() {
    try {
      const payload = pruneEmpty({ ...form, line_items: lines });
      await http.post("/ops/proposals", payload);
      toast.success("Proposal created"); setOpen(false); onCreated();
      setForm({ title: "", intro: "", valid_until: "", notes: "" });
      setLines([{ description: "", quantity: 1, unit_price_cents: 0 }]);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="new-proposal-btn"><Plus size={14} className="mr-1" /> New proposal</Button></DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Create proposal</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="new-proposal-title" /></div>
          <div><Label className="text-xs">Intro / summary</Label><Textarea rows={2} value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} /></div>
          <div className="border rounded-lg p-3 space-y-2">
            <div className="text-xs font-semibold uppercase text-muted-foreground">Line items</div>
            {lines.map((li, i) => (
              <div key={i} className="grid grid-cols-12 gap-2">
                <Input className="col-span-6" placeholder="Description" value={li.description} onChange={(e) => updateLine(i, "description", e.target.value)} data-testid={`prop-line-desc-${i}`} />
                <Input className="col-span-2" type="number" placeholder="Qty" value={li.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} />
                <Input className="col-span-3" type="number" placeholder="Cents/unit" value={li.unit_price_cents} onChange={(e) => updateLine(i, "unit_price_cents", e.target.value)} data-testid={`prop-line-price-${i}`} />
                <Button variant="ghost" size="icon" className="col-span-1" onClick={() => setLines(lines.filter((_, x) => x !== i))} disabled={lines.length <= 1}><Trash2 size={14} /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setLines([...lines, { description: "", quantity: 1, unit_price_cents: 0 }])}><Plus size={12} className="mr-1" /> Add line</Button>
            <div className="text-right font-display font-bold text-primary">Total: ${(total / 100).toLocaleString()}</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Valid until</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
          </div>
          <div><Label className="text-xs">Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <Button onClick={submit} disabled={!form.title || lines.some((l) => !l.description)} className="w-full bg-primary hover:bg-primary/90" data-testid="new-proposal-save">Create draft</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
